import { useTranslation } from '@/hooks/useTranslation'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { ReactElement } from 'react'

interface ConfirmModalUIProps {
  title?: string
  /** What is about to be deleted, shown above the warning so it can be checked. */
  name?: string
  text?: string
  cancelText?: string
  confirmText?: string
  onClose?: () => void
  onConfirm?: () => void
  isLoading?: any
  children?: ReactElement
  sx?: SxProps
}
const ConfirmDeleteModalUI = ({
  title,
  name,
  text,
  cancelText,
  confirmText,
  onClose,
  onConfirm,
  children,
  isLoading,
  sx,
}: ConfirmModalUIProps) => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        ...sx,
      }}
    >
      {title && (
        <DialogTitle sx={{ padding: '41px 152px 4px 34px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
              minHeight: '24px',
              minWidth: '192px',
            }}
          >
            {title}
          </Typography>
        </DialogTitle>
      )}
      <DialogContent sx={{ paddingLeft: '34px', paddingBottom: '18px' }}>
        {name && (
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '1.3',
              maxWidth: '314px',
              marginBottom: '6px',
              // A name can be long and has no spaces to break on, so it wraps
              // mid-word rather than widening the dialog.
              overflowWrap: 'anywhere',
            }}
          >
            {name}
          </Typography>
        )}
        {text && (
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '1.3',
              maxWidth: '314px',
              minHeight: '45px',
            }}
          >
            {text}
          </Typography>
        )}
        {children && children}
      </DialogContent>
      <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
        {onClose && (
          <Button
            variant="secondary"
            sx={{ width: '99px' }}
            onClick={() => onClose()}
          >
            {cancelText || t('commonActionsCancel')}
          </Button>
        )}
        {onConfirm && (
          <LoadingButton
            loading={isLoading}
            color="primary"
            size="small"
            variant="primary"
            onClick={onConfirm}
            sx={{ width: '99px' }}
          >
            {confirmText || t('commonActionsConfirm')}
          </LoadingButton>
        )}
      </DialogActions>
    </Box>
  )
}

export default ConfirmDeleteModalUI
